import "./InboxPage.scss";

import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../utils/api";
import usePageTitle from "../../hooks/PageTitle";
import { timeAgo, formatChatDay } from "../../utils/time";
import Avatar from "../../components/Avatar/Avatar";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShare, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useCurrentUser } from "../../UserProvider";
import { Link, useParams } from "react-router-dom";

export default function InboxPage() {
  usePageTitle("Inbox");

  const { currentUser } = useCurrentUser();
  const { display_name: targetDisplayName } = useParams();

  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loadingChats, setLoadingChats] = useState(true);
  const [activeChat, setActiveChat] = useState(null);

  const bottomRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const { data } = await api.get(`/direct_messages/chats`);

        if (targetDisplayName) {
          const matchIndex = data.findIndex(c => c.display_name === targetDisplayName);

          if (matchIndex !== -1) {
            const [matched] = data.splice(matchIndex, 1);
            const ordered = [matched, ...data];
            setChats(ordered);
            setActiveChat(matched);
          } else {
            try {
              const { data: profile } = await api.get(`/profiles/display_name/${targetDisplayName}`);
              const newChat = {
                user_id: profile.user_id,
                name: profile.name,
                display_name: profile.display_name,
                avatar_url: profile.avatar_url,
                last_message_at: null,
                last_message_text: null,
                unread_count: 0,
              };
              setChats([newChat, ...data]);
              setActiveChat(newChat);
            } catch (profileErr) {
              console.error("Failed to fetch profile", profileErr);
              setChats(data);
            }
          }
        } else {
          setChats(data);
        }
      } catch (err) {
        console.error("Failed to fetch chats", err);
      } finally {
        setLoadingChats(false);
      }
    };

    fetchChats();
  }, [targetDisplayName]);

  // LOAD MESSAGES (initial + when chat changes)
  useEffect(() => {
    if (!activeChat) return;

    let isMounted = true;

    const loadMessages = async () => {
      if (activeChat.user_id) {
        try {
          const { data } = await api.get(`/direct_messages`, {
            params: { user_id: activeChat.user_id },
          });

          if (!isMounted) return;

          setMessages(data.slice().reverse());
        } catch (err) {
          console.error("Failed to load messages", err);
        }
      } else {
        setMessages([
          {
            id: Date.now(),
            text: activeChat.last_message_text,
            sender: "them",
            created_at: new Date().toISOString(),
          },
        ]);
      }
    };

    loadMessages();

    return () => {
      isMounted = false;
    };
  }, [activeChat]);

  // Polling for active chat
  useEffect(() => {
    if (!activeChat || !activeChat.user_id) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/direct_messages`, {
          params: { user_id: activeChat.user_id },
        });

        const newMessages = data.slice().reverse();

        setMessages((prev) => {
          // Avoid unnecessary re-renders if nothing changed
          if (
            prev.length === newMessages.length &&
            prev[prev.length - 1]?.id === newMessages[newMessages.length - 1]?.id
          ) {
            return prev;
          }
          return newMessages;
        });
      } catch (err) {
        console.error("Polling failed", err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [activeChat]);

  // smooth scroll whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const getChatKey = (chat) =>
    chat.user_id ?? chat.email ?? chat.name;

  const handleSendMessage = async () => {
    if (!message.trim() || !activeChat) return;

    const tempId = Date.now();

    const optimisticMessage = {
      id: tempId,
      text: message,
      sender: "me",
      sender_id: currentUser.id,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setMessage("");

    setTimeout(scrollToBottom, 0);

    try {
      const { data } = await api.post(`/direct_messages`, {
        receiver_id: activeChat.user_id,
        text: optimisticMessage.text,
      });

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId ? { ...data, sender: "me" } : msg
        )
      );
    } catch (err) {
      console.error("Failed to send message", err);

      setMessages((prev) =>
        prev.filter((msg) => msg.id !== tempId)
      );
    }
  };

  return (
    <div className="__inbox">
      <div
        className={classNames("content", {
          "has-active-chat": !!activeChat,
          "no-active-chat": !activeChat,
        })}
      >

        {/* LEFT SIDE */}
        <div className="chats">
          <div className="title">Chats</div>

          <div className="chat-list">
            {loadingChats && (
              <div className="empty-wrapper glass">
                <div className="empty">Loading chats...</div>
              </div>
            )}

            {!loadingChats && chats.length === 0 && (
              <div className="empty-wrapper glass">
                <div className="empty">No conversations yet.</div>
              </div>
            )}

            {!loadingChats &&
              chats.map((chat) => {
                const key = getChatKey(chat);
                const isActive =
                  activeChat && getChatKey(activeChat) === key;

                return (
                  <div
                    key={key}
                    className={classNames("chat-item", "glass", {
                      active: isActive,
                    })}
                    onClick={() => setActiveChat(chat)}
                  >
                    <Avatar profile={chat} className="sender-avatar" />

                    <div className="chat-content">
                      <div className="sender-name">{chat.name}</div>

                      <div className="chat-details">
                        <div className="chat-text">
                          {chat.last_message_text}
                        </div>

                        <div className="chat-date">
                          {timeAgo(chat.last_message_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="messages">
          <div className="sender">
            {activeChat && (
              <div className="sender-details glass">
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="back-caret"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveChat(null);
                  }}
                />

                {activeChat.display_name ? (
                  <Link
                    to={`/p/${encodeURIComponent(activeChat.display_name)}`}
                    className="sender-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Avatar profile={activeChat} className="sender-avatar" />
                    {activeChat.name}
                  </Link>
                ) : (
                  <>
                    <Avatar profile={activeChat} className="sender-avatar" />
                    {activeChat.name}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="message-list glass">
            {activeChat && (
              <>
                <div className="messages-list">
                  {(() => {
                    let currentDate = null;

                    return messages.map((msg, index) => {
                      const msgDate = new Date(msg.created_at).toDateString();
                      const showDate = msgDate !== currentDate;
                      currentDate = msgDate;

                      const isMine = msg.sender_id === currentUser?.id;

                      const next = messages[index + 1];
                      const nextIsSameSender =
                        next && next.sender_id === msg.sender_id;

                      const showAvatar = !nextIsSameSender;

                      return (
                        <div key={msg.id} className="message-block">
                          {showDate && (
                            <div className="date-header">
                              {formatChatDay(msg.created_at)}
                            </div>
                          )}

                          <div
                            className={classNames("message", {
                              mine: isMine,
                              theirs: !isMine,
                            })}
                          >
                            {msg.text}
                          </div>

                          {showAvatar && (
                            <div
                              className={classNames("message-avatar-wrapper", {
                                mine: isMine,
                                theirs: !isMine,
                              })}
                            >
                              <Avatar
                                profile={
                                  isMine ? currentUser?.profile : activeChat
                                }
                                className="message-avatar"
                              />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>

                <div ref={bottomRef} />
              </>
            )}
          </div>

          <div className="send-menu glass">
            { activeChat && activeChat.user_id &&
              <>
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />

                <button className="send" onClick={handleSendMessage}>
                  <FontAwesomeIcon icon={faShare} />
                </button>
              </>
            }
          </div>
        </div>
      </div>
    </div>
  );
}